// Design tokens — Train Smart AI
// =================================================
// Source of truth for the visual language. Reference component is
// WeeklyPlanRenderer (the most polished surface in the app); every other
// screen pulls from these tokens so colors, spacing and badge styles stay
// in lockstep when the palette evolves.
//
// Two ways to consume:
//   1. `components.card`        — full Tailwind className string for direct use.
//   2. `components.badge.cyan`  — same, grouped by family for clarity.
// Reusable React atoms (CoachNote / IntensityBadge / RpeBar) read from this
// file so changing a color here updates every instance across the app.

// ── Color palette ────────────────────────────────────────────────────
export const colors = {
  bg: {
    primary:   '#0f172a',  // slate-900 — page background
    secondary: '#1e293b',  // slate-800 — card surfaces
    hover:     '#283548',  // hover state for interactive cards
  },
  // Accent hues used across the app. Names match the meaning, not the hex.
  accent: {
    cyan:    'cyan-400',     // headers, primary info, running
    blue:    'blue-500',     // primary CTAs
    purple:  'purple-400',   // coach messages / B2B
    orange:  'orange-400',   // cycling
    emerald: 'emerald-400',  // success, easy running, build phase
    amber:   'amber-400',    // warnings, coach notes, taper phase
    red:     'red-400',      // VO2max, errors
    rose:    'rose-400',     // intervals
  },
  text: {
    primary:   'white',       // headings + primary data
    secondary: 'gray-300',    // body copy
    muted:     'gray-400',    // labels / secondary info
    disabled:  'gray-500',    // captions / meta
    veryMuted: 'gray-600',    // dev-only labels
  },
  status: {
    success: 'emerald-500',
    warning: 'amber-500',
    error:   'red-500',
    info:    'cyan-500',
  },
};

// ── Components — ready-to-paste className strings ────────────────────
// Use these directly: `<div className={components.card}>...</div>`.
// Grouped by family so the IDE autocompletes useful neighbors.
export const components = {
  // Card surfaces.
  card:      'bg-[#1e293b] border border-white/10 rounded-2xl p-4',
  cardHover: 'bg-[#1e293b] border border-white/10 rounded-2xl p-4 hover:bg-[#283548] transition-colors cursor-pointer',
  cardRest:  'bg-slate-800/30 border border-slate-700/30 rounded-2xl p-4 opacity-75 hover:opacity-100 transition',

  // Buttons.
  btnPrimary:   'w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold hover:opacity-90 transition-all',
  btnSecondary: 'w-full py-3 rounded-xl bg-white/10 border border-white/20 text-gray-300 font-medium hover:bg-white/15 transition-all',
  btnDanger:    'w-full py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/30 transition-all',
  btnChip:      'px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 text-gray-300 text-sm hover:bg-white/15 transition-all',

  // Form inputs.
  input:    'w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-colors',
  inputSm:  'w-full px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-white placeholder-gray-500 text-sm focus:border-cyan-400 focus:outline-none transition-colors',

  // Badges — keep this map in sync with IntensityBadge palette keys.
  badge: {
    cyan:    'px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-medium border border-cyan-500/30',
    emerald: 'px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium border border-emerald-500/30',
    teal:    'px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400 text-xs font-medium border border-teal-500/30',
    blue:    'px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium border border-blue-500/30',
    orange:  'px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-medium border border-orange-500/30',
    amber:   'px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium border border-amber-500/30',
    purple:  'px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium border border-purple-500/30',
    red:     'px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium border border-red-500/30',
    rose:    'px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 text-xs font-medium border border-rose-500/30',
    slate:   'px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400 text-xs font-medium border border-slate-500/30',
  },

  // Status alerts — use for inline messages (success / warning / error).
  alertSuccess: 'p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm',
  alertWarning: 'p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm',
  alertError:   'p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm',
  alertInfo:    'p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm',

  // Coach note (highlighted tactical block). Default = tactical/amber.
  coachNote:        'p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm',
  coachNoteWarning: 'p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm',
  coachNoteInfo:    'p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-sm',
};

// ── Shared card separation ───────────────────────────────────────────
// One subtle, neutral separator so every card reads as a defined block — a thin white border
// (0.08, not a bright line) + a soft shadow, and NO background-colour change. Single source of
// truth: tune the opacity HERE and every card that uses it updates together.
//
// CONVENTION (decided 2026-07-20): PARENT / info cards (pace prediction, AI insights, activity
// list, weekly-plan/coach cards) use THIS neutral token. Small SEMANTIC stat tiles keep their
// meaning-coded COLOURED borders (running=cyan, success=emerald, warning=amber) — do NOT flatten
// those to neutral. Neutral token = "a defined block"; coloured border = "this block MEANS X".
//
// Two forms, same values:
//   • cardBorder      — inline-style object: style={{ ...cardBorder, ...rest }}   (for style-based cards)
//   • cardBorderClass — Tailwind className:  className={`${cardBorderClass} ...`} (for class-based cards)
export const cardBorder = {
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  boxShadow: '0 0 0 1px rgba(255,255,255,0.03), 0 4px 12px rgba(0,0,0,0.3)',
};
export const cardBorderClass =
  'rounded-2xl border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_4px_12px_rgba(0,0,0,0.3)]';

// ── Workout type → badge color mapping ───────────────────────────────
// Used by DayCard.colorForType and any other surface that wants to
// pick a badge color for a workout-type label.
export const workoutTypeColors = {
  'tempo':     'purple',
  'טמפו':      'purple',
  'intervals': 'rose',
  'אינטרוולים': 'rose',
  'long':      'teal',
  'ארוכה':     'teal',
  'easy':      'emerald',
  'קלה':       'emerald',
  'recovery':  'slate',
  'התאוששות':  'slate',
  'race':      'amber',
  'מרוץ':      'amber',
  'hills':     'orange',
  'עליות':     'orange',
};

// ── Training phase → badge color ─────────────────────────────────────
export const phaseColors = {
  'base':          'purple',
  'build':         'blue',
  'peak':          'emerald',
  'medium_taper':  'amber',
  'light_taper':   'amber',
  'race_week':     'red',
};

// ── Training zone → badge color (matches ZoneBadge) ──────────────────
export const zoneColors = {
  'Zone 1':    'green',
  'Zone 2':    'emerald',
  'Zone 3':    'teal',
  'Threshold': 'orange',
  'VO2max':    'red',
  'Intervals': 'rose',
  'Race Pace': 'purple',
  'Recovery':  'slate',
};

// ── Typography scale ─────────────────────────────────────────────────
// Floor: 14px for primary content (rule: no important text below 14px).
export const typography = {
  // Pace / km values — primary numeric data
  paceValue: 'font-mono font-semibold text-sm text-white tabular-nums',
  paceLabel: 'text-[13px] text-gray-400',
  // Big stat (used in HeroCard, Race countdown, etc.)
  statBig:   'text-3xl font-bold text-white leading-none',
  statLabel: 'text-gray-500 text-xs',
  // Section titles
  sectionTitle: 'text-cyan-300 text-sm font-bold',
};

// ── A2 design system (2026-08-25 redesign handoff) ───────────────────
// Single source of truth for the redesigned Home (A2) + Plan (P1) screens.
// Exact values from design_handoff_home_a2/README.md — do not eyeball-tune.
export const a2 = {
  color: {
    bg: '#0f172a',
    card: '#1e293b',
    inset: '#0f172a',
    privileged: '#161f33',
    heroGrad: 'linear-gradient(150deg, #0e2438, #0f172a 70%)',
    chatGrad: 'linear-gradient(150deg, #0e2a33, #122e38)',
    btnGrad: 'linear-gradient(to left, #2563eb, #22d3ee)',
    cyan: '#22d3ee',
    cyanText: '#38bdf8',
    green: '#34d399',
    pink: '#f472b6',
    amber: '#fbbf24',
    hardOrange: '#fb923c',
    indigo: '#818cf8',
    textPrimary: '#f8fafc',
    textTitle: '#ffffff',
    textSecondary: '#e2e8f0',
    textBody: '#cbd5e1',
    textMuted: '#94a3b8',
    textDisabled: '#64748b',
    textFaint: '#475569',
    border: 'rgba(255,255,255,0.09)',
    hairline: 'rgba(255,255,255,0.07)',
    borderSubtle: 'rgba(255,255,255,0.05)',
    borderAccent: 'rgba(56,189,248,0.28)',
    track: '#1e293b',
    trackDot: '#334155',
  },
  font: {
    ui: "'Heebo', -apple-system, sans-serif",
    display: "'Frank Ruhl Libre', serif",
    mono: "'Azeret Mono', ui-monospace, monospace",
  },
  shadow: {
    card: '0 4px 12px rgba(0,0,0,0.3)',
    hero: '0 4px 20px rgba(0,0,0,0.35)',
  },
};

// Workout type → A2 semantic color + Hebrew label (single source for Home + Plan).
export const a2WorkoutType = (raw) => {
  const t = String(raw || '').toLowerCase();
  if (/אינטרוול|interval|vo2|ריפיטישן|repetition|פארטלק|fartlek/.test(t))
    return { color: '#f472b6', label: 'אינטרוולים' };
  if (/סף|טמפו|tempo|threshold|קרוז|cruise/.test(t))
    return { color: '#fbbf24', label: 'סף / טמפו' };
  if (/ארוכ|long/.test(t))
    return { color: '#818cf8', label: 'ריצה ארוכה' };
  if (/מנוחה|rest/.test(t))
    return { color: '#64748b', label: 'מנוחה' };
  if (/מרוץ|race/.test(t))
    return { color: '#fbbf24', label: 'מרוץ' };
  return { color: '#38bdf8', label: 'ריצה קלה' };
};

// Training-phase (getTrainingPhase Hebrew names) → A2 segmented-meter position.
// Segments: 0 בסיס · 1 בנייה · 2 שיא · 3 טייפר · 4 מרוץ — the real phase set
// (user request 2026-08-25: taper and race week are distinct stages, not folded
// into "חידוד").
export const a2PhaseIndex = (phaseName) => {
  const p = String(phaseName || '');
  if (p.includes('בסיס')) return 0;
  if (p.includes('בניית יכולות') || p.includes('בנייה')) return 1;
  if (p.includes('חידוד ושיא') || p.includes('שיא') || p.includes('חידוד')) return 2;
  if (p.includes('טייפר')) return 3;
  if (p.includes('תחרות') || p.includes('מרוץ')) return 4;
  return null; // שמירת כושר / אין מרוץ — the meter hides
};

// Default export — keep imports simple.
const designTokens = { colors, components, cardBorder, cardBorderClass, workoutTypeColors, phaseColors, zoneColors, typography, a2, a2WorkoutType, a2PhaseIndex };
export default designTokens;
